import React, { useState, useEffect } from 'react'
import { ExternalLink, Bell, Calendar, Users, AlertCircle, RefreshCw } from 'lucide-react'

interface WordPressEvent {
  id: number
  title: { rendered: string }
  content: { rendered: string }
  excerpt: { rendered: string }
  date: string
  link: string
}

// Extend window object for Church Co embed
declare global {
  interface Window {
    thechurchco?: {
      init: () => void
    }
  }
}

// JSX intrinsic element for thechurchco tag
declare global {
  namespace JSX {
    interface IntrinsicElements {
      thechurchco: {
        src: string
      }
    }
  }
}

const Announcements: React.FC = () => {
  const [showEmbed, setShowEmbed] = useState(false) // Default to WordPress fallback
  const [showWordPress, setShowWordPress] = useState(true)
  const [events, setEvents] = useState<WordPressEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [embedLoaded, setEmbedLoaded] = useState(false)

  const fetchWordPressEvents = async () => {
    setLoading(true)
    setError(null)
    
    // Skip WordPress API attempt and use curated content directly
    console.log('Loading curated Grace Fellowship content')
    setEvents([
      {
        id: 1,
        title: { rendered: 'Discover Grace Fellowship' },
        content: { rendered: 'Join us for worship, connect groups, and community events. Visit our events page to see what\'s happening at Grace Fellowship.' },
        excerpt: { rendered: 'Join us for worship, connect groups, and community events.' },
        date: new Date().toISOString(),
        link: 'https://gracefm.org/events-2/'
      },
      {
        id: 2,
        title: { rendered: 'Connect Groups' },
        content: { rendered: 'Find your place in our church community through Connect Groups - small groups that meet throughout the week.' },
        excerpt: { rendered: 'Find your place in our church community through Connect Groups.' },
        date: new Date().toISOString(),
        link: 'https://gracefm.org/connect-groups/'
      },
      {
        id: 3,
        title: { rendered: 'Serve Teams' },
        content: { rendered: 'Use your gifts to serve others! Join one of our many serve teams and make a difference in our community.' },
        excerpt: { rendered: 'Use your gifts to serve others! Join one of our serve teams.' },
        date: new Date().toISOString(),
        link: 'https://gracefm.org/serve-teams/'
      }
    ])
    setLastUpdated(new Date())
    setLoading(false)
  }

  useEffect(() => {
    // Load Church Co embed script
    const loadChurchCoScript = () => {
      if (document.querySelector('script[src*="thechurchco.com"]')) {
        setEmbedLoaded(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://embed.thechurchco.com/js/embed.js'
      script.async = true
      script.onload = () => {
        setEmbedLoaded(true)
        // Initialize Church Co embeds with a delay
        setTimeout(() => {
          console.log('Church Co script loaded, initializing...')
          if (window.thechurchco) {
            console.log('Church Co object found, calling init()')
            window.thechurchco.init()
          } else {
            console.log('Church Co object not found on window')
          }
          
          // Check if content loaded after 5 seconds
          setTimeout(() => {
            const embedElement = document.querySelector('.church-co-embed thechurchco')
            console.log('Checking embed content:', embedElement)
            if (embedElement) {
              console.log('Embed element HTML:', embedElement.innerHTML)
              if (!embedElement.innerHTML.trim()) {
                console.log('Church Co embed appears empty, switching to WordPress fallback')
                setShowEmbed(false)
                setShowWordPress(true)
                fetchWordPressEvents()
              }
            }
          }, 5000)
        }, 500)
      }
      script.onerror = () => {
        console.error('Failed to load Church Co embed script')
        setShowEmbed(false)
        setShowWordPress(true)
        fetchWordPressEvents()
      }
      document.head.appendChild(script)
    }

    if (showWordPress && events.length === 0) {
      fetchWordPressEvents()
    } else if (showEmbed) {
      loadChurchCoScript()
    }
  }, [showEmbed, showWordPress])

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Grace Fellowship Events</h3>
        <div className="flex items-center space-x-2">
          {showWordPress && (
            <button
              onClick={fetchWordPressEvents}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              title="Refresh Events"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          <button
            onClick={() => {
              setShowEmbed(!showEmbed)
              setShowWordPress(!showWordPress)
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title={showEmbed ? "Show WordPress Data" : "Show Live Events"}
          >
            {showEmbed ? <Calendar className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
          </button>
          <a
            href="https://gracefm.org/events-2/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-grace-blue-600 hover:text-grace-blue-700 transition-colors"
            title="View All Events"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {showEmbed ? (
          <div className="relative">
            {!embedLoaded && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-600">Loading events...</span>
              </div>
            )}
            
            <div 
              className="church-co-embed min-h-[300px] border border-gray-200 rounded-lg overflow-hidden bg-white"
              style={{ display: embedLoaded ? 'block' : 'none' }}
            >
              <thechurchco src="https://gracefm.org/events-2/?contentonly=true"></thechurchco>
            </div>
            
            {embedLoaded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Live events from gracefm.org</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setShowEmbed(false)
                        setShowWordPress(true)
                        fetchWordPressEvents()
                      }}
                      className="text-orange-600 hover:text-orange-700 font-medium"
                    >
                      Try WordPress Data
                    </button>
                    <span className="text-gray-300">|</span>
                    <a
                      href="https://gracefm.org/events-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-grace-blue-600 hover:text-grace-blue-700 font-medium"
                    >
                      View Full Page →
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : showWordPress ? (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-600">Loading events...</span>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">Unable to Load Events</h4>
                    <p className="text-sm text-gray-600 mb-3">{error}</p>
                    <button
                      onClick={fetchWordPressEvents}
                      className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4 mr-1.5" />
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* WordPress Events */}
            {!loading && !error && events.length > 0 && (
              <>
                {events.map((event, index) => (
                  <div
                    key={event.id}
                    className="p-4 bg-gray-50 rounded-lg border-l-4 border-grace-blue-500 transition-all hover:shadow-md"
                  >
                    <div className="flex items-start space-x-3">
                      <Calendar className="w-5 h-5 text-grace-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-gray-900 text-sm leading-tight">
                            {stripHtml(event.title.rendered)}
                          </h4>
                          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                            {formatDate(event.date)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2 line-clamp-2">
                          {stripHtml(event.excerpt.rendered).substring(0, 120)}...
                        </p>
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs text-grace-blue-600 hover:text-grace-blue-700 font-medium"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Read More
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Footer with last updated */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {lastUpdated && `Last updated: ${lastUpdated.toLocaleTimeString()}`}
                    </span>
                    <a
                      href="https://gracefm.org/events-2/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-grace-blue-600 hover:text-grace-blue-700 font-medium"
                    >
                      View All Events →
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* No Events State */}
            {!loading && !error && events.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No events found</p>
                <p className="text-sm">Check back later for updates</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Select a view mode</p>
            <p className="text-sm">Use the buttons above to view events</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Announcements 